/**
 * Общие типы и константы страницы источников сканирования DAR — тот же
 * приём, что и types.ts в DataMap (там — SENSITIVITY_COLORS): презентационная
 * карта по значению типа из API, которой пользуется больше одного файла
 * каталога, живёт в отдельном не-компонентном файле. Не в модуле вызовов
 * (api/dataSources.ts отвечает только за контракт с сервером — DataSourceType
 * оттуда сюда только импортируется, не переопределяется) и не в файле
 * конкретного компонента (react-refresh/only-export-components запрещает
 * экспорт констант из файла, который экспортирует ещё и компонент).
 */

import type { DataSourceType } from '@/api/dataSources';
import type { DatabaseInstanceType } from '@/api/sqlDb';
import type { NosqlInstanceType } from '@/api/nosqlDb';

/**
 * Отображаемые названия типов. Не заведены через i18n: это названия
 * конкретных продуктов (PostgreSQL, MySQL и т.д.), а не текст интерфейса —
 * тот же приём, что и protocolOptions в CreateExternalConnection.tsx.
 * Список и порядок — из doc-comment DataSourceType (src/api/dataSources.ts).
 * Потребители: DataSourceForm.tsx (выбор типа) и DataSources/index.tsx
 * (колонка "Тип").
 */
export const TYPE_LABELS: Record<DataSourceType, string> = {
	postgres: 'PostgreSQL',
	mysql: 'MySQL',
	mssql: 'MS SQL',
	mongo: 'MongoDB',
	cassandra: 'Cassandra',
	s3: 'S3',
	kafka: 'Kafka',
};

/**
 * Соответствие типа SQL-инстанса (DatabaseInstanceType, каталог инвентаря
 * sql-db) типу источника сканирования DAR — разные перечисления одного и
 * того же по историческим причинам (инвентарь и источники сканирования
 * выросли из разных модулей бэкенда). Partial, а не
 * Record<DatabaseInstanceType, ...>: InfrastructureService.sqlDbTypePrettyName
 * на бэкенде отдаёт девять типов, а здесь — три записи. Четыре из шести
 * отсутствующих (Oracle, MariaDB, SQLite, DB2) отсутствуют потому, что для
 * них источника DAR не существует вовсе — подключение показать нечем ни в
 * каком месте интерфейса. Оставшиеся два (MongoDB, Amazon Neptune)
 * отсутствуют по другой причине: источник DAR для Mongo у системы есть
 * (см. TYPE_LABELS.mongo выше), но DatabaseInstanceType — это перечень
 * именно SQL-инстансов каталога sql-db, а Mongo и Neptune туда не входят
 * (свой каталог инвентаря, вне этой карты); Neptune источника DAR не имеет
 * вовсе, как и первые четыре. Разбор по каждому не хранится отдельно —
 * важен факт, что список бэкенда шире списка карты по обеим причинам
 * сразу, а не только по одной, и обращение по несуществующему здесь ключу
 * обязано типизироваться как возможный undefined, а не молча считаться
 * гарантированным значением.
 * Потребитель: DataStorages/SqlInstancesTab.tsx (действие "Подключить" у
 * обнаруженного, но ещё не подключённого инстанса) — живёт здесь, а не там,
 * по той же причине, что и TYPE_LABELS (react-refresh/only-export-components),
 * и чтобы тесты SqlInstancesTab.tsx могли параметризоваться по фактическому
 * набору поддерживаемых типов, а не по литеральному списку, рассинхронизация
 * с которым осталась бы незамеченной.
 */
export const INSTANCE_TYPE_TO_SOURCE_TYPE: Partial<Record<DatabaseInstanceType, DataSourceType>> = {
	PostgreSQL: 'postgres',
	MySQL: 'mysql',
	'MS SQL Server': 'mssql',
};

/**
 * Соответствие типа NoSQL-инстанса (NosqlInstanceType, каталог инвентаря
 * nosql-db) типу источника сканирования DAR — тот же приём и та же причина
 * жить здесь, что и у INSTANCE_TYPE_TO_SOURCE_TYPE выше, для документных
 * СУБД вместо SQL. Отображение НЕ по совпадению имён: NosqlInstanceType
 * хранит уже готовое к показу имя ("MongoDB"/"Cassandra",
 * InfrastructureService.noSqlDbTypePrettyName, qhub-analytics), а не код
 * конвейера ("mongodb"/"cassandra") — важно не перепутать регистр при
 * будущей правке, обращение по "mongodb" в нижнем регистре не совпадёт ни с
 * одним реальным значением поля type и молча вернёт undefined. Mongo при
 * этом меняет ещё и само имя (MongoDB -> 'mongo', имя константы
 * DataSourceType, а не "mongodb"), Cassandra — нет (Cassandra -> 'cassandra'
 * в обоих словарях). Record, а не Partial<Record<...>> (в отличие от
 * INSTANCE_TYPE_TO_SOURCE_TYPE выше, где бэкенд отдаёт куда больше SQL-типов,
 * чем знает фронтенд): конвейер обнаружения сегодня выдаёт
 * NoSqlDbInstanceEntity.type ровно двух видов (DarSourceService.
 * noSqlInstanceType -> NoSqlDbType.getValue()), и NosqlInstanceType — их
 * точный перечень, без более широкого набора закрытых типов бэкенда, как у
 * SQL. Потребитель: DataStorages/NoSqlInstancesTab.tsx.
 */
export const NOSQL_INSTANCE_TYPE_TO_SOURCE_TYPE: Record<NosqlInstanceType, DataSourceType> = {
	MongoDB: 'mongo',
	Cassandra: 'cassandra',
};

/**
 * Имя query-параметра адреса, которым ссылка на конфликт в DataSourceForm.tsx
 * (errors.duplicate, при попытке завести дубликат) помечает карточку уже
 * существующего источника, а DataSources/index.tsx читает его же, чтобы
 * подсветить эту строку в таблице. Единственное значение, разделяемое обеими
 * сторонами: при рассинхронизации (форма ставит один параметр, страница
 * читает другой) ссылка обещала бы переход, которого не происходит, и это
 * не поймать раздельными тестами каждой стороны — контракт держится именно
 * на общей константе, а не на совпадении литералов по памяти.
 * Потребители: DataSourceForm.tsx (пишет), DataSources/index.tsx (читает).
 */
export const HIGHLIGHT_PARAM = 'highlight';

/** Сенсор DAR, по которому отобран список: приходит из окна сенсора на странице «Сенсоры» */
export const SENSOR_PARAM = 'sensor';
